/**
 * Cross-Selling — der zweite Lauf nach der Anlage, im aufgeklappten Lauf der Historie.
 *
 * 🔴 Matze, 30.09.2026: „Nachdem alle gewünschten Artikel eines Laufs angelegt sind, einen
 * zweiten Lauf machen, in dem wir das Cross-Selling definieren." Teamline-Artikel bekommen
 * Artikel derselben Linie (beide Richtungen), alle anderen Artikel der gleichen Kategorie
 * in ±30 % Preislage — höchstens 5 je Artikel.
 *
 * Seit 01.10.2026 läuft das automatisch direkt nach jedem Anlege-Lauf (Matze); die Knöpfe
 * bleiben zum Nachsehen und Wiederholen (ein zweiter Lauf gibt nur „schon da").
 *
 * Zwei Knöpfe, zwei Jobs:
 *   1. „Vorschau berechnen" liest nur und speichert den Plan am Lauf.
 *   2. „Cross-Selling setzen" schreibt GENAU diesen Plan (Tippbestätigung VERKNUEPFEN).
 * Alle Pfade OHNE `/api/`-Prefix — der Core-api-Client hängt ihn selbst an.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link2, ListChecks } from 'lucide-react';
import { api } from '@libero/core/services/api';
import { useJobStatus } from '@libero/core/hooks/useJobStatus';
import LaufButton from './LaufButton';
import Info from './Info';
import { euro, zahl, zeitpunkt } from '../lib/format';
import type { CrossSellingStand } from '../lib/typen';

const ART: Record<string, string> = {
  teamline: 'Teamline',
  kategorie: 'Kategorie ±30 %',
  rueck: 'Rücklink (Bestand)',
};

export default function CrossSellingPanel({ laufId }: { laufId: number }) {
  const [stand, setStand] = useState<CrossSellingStand | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [bestaetigung, setBestaetigung] = useState('');
  const [alleZeigen, setAlleZeigen] = useState(false);
  const planJob = useJobStatus(null);
  const setzJob = useJobStatus(null);

  const laden = useCallback(async () => {
    try {
      const antwort = await api.get<{ data: CrossSellingStand }>(`/programmes/artikelimport/laeufe/${laufId}/cross-selling`);
      setStand(antwort.data.data);
    } catch (e: any) {
      setFehler(e?.response?.data?.error?.message ?? 'Der Cross-Selling-Stand ließ sich nicht laden.');
    }
  }, [laufId]);

  useEffect(() => { void laden(); }, [laden]);
  // Nach jedem fertigen Job den gespeicherten Stand neu lesen.
  useEffect(() => {
    if (planJob.status === 'completed' || planJob.status === 'failed'
      || setzJob.status === 'completed' || setzJob.status === 'failed') void laden();
  }, [planJob.status, setzJob.status, laden]);

  const starte = async (job: typeof planJob, pfad: string, body: Record<string, unknown>) => {
    setFehler(null);
    try {
      await job.startJob(pfad, body);
    } catch (e: any) {
      setFehler(e?.response?.status === 409 ? 'Läuft bereits.' : (e?.response?.data?.error?.message ?? 'Start fehlgeschlagen.'));
    }
  };

  const plan = stand?.plan;
  const links = plan?.links.length ?? 0;
  const rueck = plan?.links.filter(l => l.art === 'rueck').length ?? 0;
  const mitPartnern = (plan?.artikel ?? []).filter(a => a.partner.length > 0 || a.hinweis);
  const gezeigt = alleZeigen ? mitPartnern : mitPartnern.slice(0, 30);
  const jobFehler = planJob.status === 'failed' ? (planJob.job?.error ?? planJob.error) : setzJob.status === 'failed' ? (setzJob.job?.error ?? setzJob.error) : null;
  const beschaeftigt = planJob.isRunning || setzJob.isRunning;

  return (
    <div className="mt-4 space-y-3 rounded border border-[#DAD9D9] bg-white p-3">
      <h3 className="flex items-center gap-2 text-sm font-medium text-[#1A1A1A]">
        <Link2 className="h-4 w-4" aria-hidden /> Cross-Selling
        <span className="font-normal text-[#6B6A66]">
          {plan
            ? `Plan vom ${zeitpunkt(plan.am)}: ${zahl(links)} Verknüpfungen${rueck > 0 ? `, davon ${zahl(rueck)} Rücklinks an Bestandsartikel` : ''}`
            : 'noch kein Plan'}
        </span>
        <Info titel="Wie wird gewählt?">
          <p>Teamline-Artikel: weitere Artikel derselben Linie, möglichst verschiedene Produktarten (eine Hose zeigt Trikot, Jacke, Polo …).</p>
          <p>Ohne Teamline: Artikel der gleichen Kategorie, Hersteller gemischt, Webshop-Preis höchstens ±30 % entfernt, nächstgelegene zuerst.</p>
          <p>Hat die Linie weniger als 5 andere Artikel, werden die freien Plätze aus der Kategorie aufgefüllt.</p>
          <p>Läuft automatisch beim „Aktivieren“ — erst dann sind die Artikel und ihre Linie aktiv. Höchstens 5 je Artikel. Teamline-Artikel nur mit ihrer Linie; freie Plätze füllen sich, wenn weitere Artikel der Linie aktiviert werden. Partner nur aktiv und im Shop anstoss24.de. Vorhandene Verknüpfungen werden nie gelöscht.</p>
          <p>Beziehung in Plenty: „Ähnlicher Artikel".</p>
        </Info>
      </h3>

      {stand?.automatisch && (
        <p className={`text-sm ${stand.automatisch.status === 'fehler' ? 'text-red-800' : stand.automatisch.status === 'laeuft' ? 'text-yellow-900' : 'text-[#1A1A1A]/70'}`}>
          {stand.automatisch.status === 'laeuft'
            ? `Läuft automatisch seit ${zeitpunkt(stand.automatisch.gestartetAm)} — ein Write je Verknüpfung, das kann dauern. „Neu laden" zeigt den Stand.`
            : stand.automatisch.status === 'fehler'
              ? `Automatischer Lauf fehlgeschlagen (${zeitpunkt(stand.automatisch.beendetAm)}): ${stand.automatisch.meldung ?? ''}`
              : `Automatisch nach dem Anlegen (${zeitpunkt(stand.automatisch.beendetAm)}): ${stand.automatisch.meldung ?? ''}`}
        </p>
      )}
      {stand?.schreiben && (
        <p className={`text-sm ${stand.schreiben.fehler > 0 ? 'text-yellow-900' : 'text-green-800'}`}>
          Gesetzt am {zeitpunkt(stand.schreiben.am)}: {zahl(stand.schreiben.gesetzt)} neu,{' '}
          {zahl(stand.schreiben.schonDa)} waren schon da, {zahl(stand.schreiben.fehler)} Fehler
          {stand.schreiben.planAm !== plan?.am && ' — der Plan wurde danach neu berechnet'}
        </p>
      )}
      {stand?.schreiben && stand.schreiben.beispiele.length > 0 && (
        <ul className="list-disc pl-5 text-xs text-red-800">
          {stand.schreiben.beispiele.map(b => <li key={b}>{b}</li>)}
        </ul>
      )}
      {plan && plan.meldungen.length > 0 && (
        <ul className="list-disc pl-5 text-xs text-yellow-900">
          {plan.meldungen.map(m => <li key={m}>{m}</li>)}
        </ul>
      )}
      {(fehler || jobFehler) && <p className="text-sm text-red-800">{fehler ?? jobFehler}</p>}

      <div className="flex flex-wrap items-end gap-3">
        <LaufButton
          laeuft={planJob.isRunning}
          laufText="Vorschau wird berechnet"
          schritt={planJob.progress?.message ?? null}
          prozent={planJob.progress?.percentage ?? null}
          dauerHinweis="liest nur — etwa 1–3 Minuten"
          onClick={() => void starte(planJob, `/programmes/artikelimport/laeufe/${laufId}/cross-selling/plan`, {})}
          disabled={beschaeftigt}
          variant="outline"
          size="sm"
          icon={<ListChecks className="mr-2 h-3.5 w-3.5" aria-hidden />}
        >
          {plan ? 'Vorschau neu berechnen' : 'Vorschau berechnen'}
        </LaufButton>

        {links > 0 && (
          <>
            <label className="text-xs">
              <span className="mb-1 block text-[#1A1A1A]/70">Zum Bestätigen <strong>VERKNUEPFEN</strong> tippen</span>
              <input
                type="text"
                value={bestaetigung}
                onChange={e => setBestaetigung(e.target.value)}
                placeholder="VERKNUEPFEN"
                className="rounded border border-[#DAD9D9] px-2 py-1 font-mono text-sm"
              />
            </label>
            <LaufButton
              laeuft={setzJob.isRunning}
              laufText="Verknüpfungen werden gesetzt"
              schritt={setzJob.progress?.message ?? null}
              prozent={setzJob.progress?.percentage ?? null}
              dauerHinweis="ein Write je Verknüpfung — bei vielen Links 1–2 Stunden"
              onClick={() => void starte(setzJob, `/programmes/artikelimport/laeufe/${laufId}/cross-selling`, { bestaetigung: 'VERKNUEPFEN' })}
              disabled={beschaeftigt || bestaetigung !== 'VERKNUEPFEN'}
              size="sm"
              className="bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d]"
              icon={<Link2 className="mr-2 h-3.5 w-3.5" aria-hidden />}
            >
              {`${zahl(links)} Verknüpfungen setzen`}
            </LaufButton>
          </>
        )}
      </div>

      {plan && mitPartnern.length > 0 && (
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-[#1A1A1A]/60">
            <tr>
              <th className="px-2 py-1">Artikel</th>
              <th className="px-2 py-1">Auswahl</th>
              <th className="px-2 py-1">Verknüpft mit</th>
            </tr>
          </thead>
          <tbody>
            {gezeigt.map(a => (
              <tr key={`${a.itemId}-${a.art}`} className="border-t border-[#DAD9D9]/60 align-top">
                <td className="px-2 py-1.5">
                  <span className="text-[#1A1A1A]">{a.name}</span>
                  <span className="ml-2 font-mono text-xs text-[#1A1A1A]/50">{a.itemId}</span>
                  {!a.neu && <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700">Bestand</span>}
                </td>
                <td className="whitespace-nowrap px-2 py-1.5 text-xs text-[#1A1A1A]/70">{a.art ? ART[a.art] : '–'}</td>
                <td className="px-2 py-1.5">
                  <div className="flex flex-wrap gap-1">
                    {a.partner.map(p => (
                      <span key={p.itemId} className="rounded bg-[#F5F5F5] px-2 py-0.5 text-xs" title={`Item ${p.itemId}`}>
                        {p.name}{p.preis != null && <span className="ml-1 text-[#1A1A1A]/50">{euro(p.preis)}</span>}
                      </span>
                    ))}
                  </div>
                  {a.hinweis && <p className="mt-0.5 text-xs text-[#6B6A66]">{a.hinweis}</p>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {plan && mitPartnern.length > gezeigt.length && (
        <button type="button" onClick={() => setAlleZeigen(true)} className="text-xs text-[#1A1A1A] underline">
          alle {zahl(mitPartnern.length)} Zeilen zeigen
        </button>
      )}
    </div>
  );
}
